import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Trophy } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const LS_KEY = "hifireels.jackpot.last_seen_id";

/**
 * JackpotBroadcast — global floating toast that shows "🎉 @user just won 100K Goldly!"
 * whenever ANY user hits the jackpot. Polls /api/spin/recent-jackpots every 30s and
 * dedupes by jackpot id via localStorage. Mounted once in the app shell (MobileLayout).
 * Plays a subtle 2-note chime via Web Audio API. Auto-hides after 8s.
 */

function playJackpotChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const notes = [880, 1174.7]; // A5 then D6 — bright ascending
    notes.forEach((freq, i) => {
      const t = ctx.currentTime + i * 0.14;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.18, t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.36);
    });
    setTimeout(() => ctx.close?.().catch(() => {}), 900);
  } catch { /* silent — audio blocked by browser autoplay policy */ }
}

export default function JackpotBroadcast() {
  const { user } = useAuth();
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const seenRef = useRef(new Set(localStorage.getItem(LS_KEY)?.split(",").filter(Boolean) || []));
  const sinceRef = useRef(Date.now() - 60000); // start 60s ago

  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/spin/recent-jackpots?since_ms=${sinceRef.current}`);
        if (stopped) return;
        sinceRef.current = data.server_now_ms || Date.now();
        for (const j of (data.jackpots || [])) {
          if (seenRef.current.has(j.id)) continue;
          if (user && j.user_id === user.id) continue; // never toast the winner themselves
          seenRef.current.add(j.id);
          const arr = Array.from(seenRef.current).slice(-30);
          localStorage.setItem(LS_KEY, arr.join(","));
          setToast(j);
          playJackpotChime();
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => { if (!stopped) setToast(null); }, 8000);
          break;
        }
      } catch { /* ignore transient errors */ }
      if (!stopped) setTimeout(poll, 30000);
    };
    setTimeout(poll, 3000);
    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return (
    <AnimatePresence>
      {toast && (
        <motion.a
          href="/spin"
          data-testid="jackpot-broadcast"
          initial={{ y: -80, opacity: 0, scale: 0.9 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: "spring", stiffness: 260, damping: 22 }}
          className="fixed top-2 left-1/2 -translate-x-1/2 z-[95] flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-gradient-to-r from-[#FBBF24] via-[#FF7A00] to-[#FF2A5F] shadow-[0_10px_35px_rgba(255,122,0,0.55)] border border-white/25 max-w-[92vw]"
        >
          <Trophy className="w-4 h-4 text-white flex-shrink-0"/>
          <img src={toast.avatar_url || "/logo192.png"} alt="" className="w-6 h-6 rounded-full border border-white/60 object-cover flex-shrink-0"/>
          <div className="text-[12px] font-outfit font-extrabold text-white leading-tight truncate">
            🎉 @{toast.user_username || "someone"} won <b className="tabular-nums">{toast.prize.toLocaleString()}</b> Goldly!
          </div>
        </motion.a>
      )}
    </AnimatePresence>
  );
}
